import useCatalogAccess from "../useCatalogAccess";
import { readStoredUser } from "../../../utils/sessionUser";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "../CatalogCreationPendingNotice";
import InternalCodeSuggestions from "../InternalCodeSuggestions";
import { catalogRequestError, duplicateInternalCodeMessage, hasInternalCodeConflict } from "../internalCatalogCodes";
import { useCatalogTaxonomyText, useCatalogTaxonomyDialog } from "../catalogTaxonomyMessages";
import { useEffect, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";

const buildEmptyForm = () => ({
  code: "",
  name: "",
  description: "",
});

export default function AddFamilyPopup({ isOpen, onClose, onAdded, existingRecords = [] }) {
  const t = useCatalogTaxonomyText();
  const businessUnitId = useRef(readStoredUser()?.active_business_unit_id).current;
  const access = useCatalogAccess(businessUnitId);
  const permission = "purchasing.items.create";
  const canCreate = access.can(permission);
  const [form, setForm] = useState(buildEmptyForm());
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState("");
  const [pendingRequest, setPendingRequest] = useState(null);
  const dialogRef = useCatalogTaxonomyDialog(isOpen && canCreate && !pendingRequest, () => handleClose(), saving);

  useEffect(() => {
    if (!isOpen || !canCreate) return;
    savingRef.current = false;
    setPendingRequest(null);
    setForm(buildEmptyForm());
    setSaving(false);
    setError("");
  }, [isOpen, canCreate, access.contextKey]);

  if (!isOpen || !canCreate) return null;
  if (pendingRequest) return <CatalogCreationPendingNotice request={pendingRequest} onClose={onClose} />;

  const handleClose = () => {
    if (savingRef.current) return;
    setForm(buildEmptyForm());
    setError("");
    onClose();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (savingRef.current || !access.can(permission)) return;
    if (hasInternalCodeConflict(form.code, existingRecords)) {
      setError(duplicateInternalCodeMessage(form.code));
      return;
    }
    const stamp = access.capture();
    savingRef.current = true;
    setSaving(true);
    setError("");

    try {
      const response = await axios.post("/api/purchasing/families", { ...form, code: form.code.trim() }, access.requestConfig(permission));
      if (!access.isCurrent(stamp, permission)) return;
      if (isPendingCatalogCreation(response.data)) { setPendingRequest(response.data.request); setSaving(false); return; }
      if (typeof onAdded === "function") {
        await onAdded(response.data);
      }
      setForm(buildEmptyForm());
      onClose();
    } catch (requestError) {
      if (!access.isCurrent(stamp, permission)) return;
      console.error("[FAMILIES][ADD][ERROR]", requestError);
      savingRef.current = false;
      setError(catalogRequestError(requestError, "Error saving family.", form.code));
      setSaving(false);
    }
  };

  return (
    <div
      className="spx-modalBackdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >


      <div ref={dialogRef} className="spx-modal" role="dialog" aria-modal="true" aria-label={t("Add family")}>
        <div className="spx-modalHeader">
          <div>
            <div className="spx-modalKicker">{t("Catalog Structure")}</div>
            <h3 className="spx-modalTitle">{t("Add Family")}</h3>
            <p className="spx-modalSubtitle">{t("Use a short code and a clear name for this top-level group.")}</p>
          </div>

          <button type="button" className="spx-closeBtn" onClick={handleClose} disabled={saving} aria-label={t("Close add family popup")}>
            x
          </button>
        </div>

        <form onSubmit={handleSubmit} className="spx-modalBody">
          <div className="spx-formGrid">
            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="family_name">{t("Family Name")} </label>
              <input
                id="family_name"
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                disabled={saving}
                className="spx-input"
                placeholder={t("Example: Cleaning Products")}
                required
              />
            </div>

            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="family_code">{t("Family Code")} </label>
              <input
                id="family_code"
                type="text"
                name="code"
                value={form.code}
                onChange={handleChange}
                disabled={saving}
                className="spx-input"
                placeholder={t("Example: F001")}
                required
              />
              <InternalCodeSuggestions name={form.name} value={form.code} resource="families" disabled={saving}
                onSuggest={(code) => { setForm((previous) => ({ ...previous, code })); setError(""); }} />
            </div>

            <div className="spx-field spx-field--full">
              <label className="spx-label" htmlFor="family_description">{t("Description")} </label>
              <textarea
                id="family_description"
                name="description"
                value={form.description}
                onChange={handleChange}
                disabled={saving}
                className="spx-textarea"
                placeholder={t("Optional context for the teams that will classify items under this family.")}
              />
            </div>
          </div>

          {error ? <div className="spx-alert" role="alert">{t(error)}</div> : null}

          <div className="spx-modalFooter">
            <button type="button" className="spx-btn spx-btn--ghost" onClick={handleClose} disabled={saving}>{t("Cancel")} </button>
            <button type="submit" className="spx-btn spx-btn--primary" disabled={saving}>
              {t(saving ? "Saving..." : "Save Family")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
